import { toCurrency } from '@/utils/numberFormatter';
import { useIntl } from 'react-intl';
import { Bar, BarChart, ResponsiveContainer, Tooltip, YAxis } from 'recharts';
import { styled } from 'stitches.config';
import { palette } from 'theme/palette';
import { SimulatorLegend } from '../simulators/SimulatorLegend';
import Typography from '../sushi/Typography';
import { MonthlyPaymentsGraphTooltip } from './MonthlyPaymentsGraphTooltip';

const PLACEHOLDER_DATA: MonthlyPaymentGraphData[] = [
  {
      "year": 1,
      "interests": 291.6666666666667,
      "capital": 300.7930513164251,
      "insurance": 12.5
  },
  {
      "year": 2,
      "interests": 280.96837436402046,
      "capital": 311.4913436190713,
      "insurance": 12.5
  },
  {
      "year": 3,
      "interests": 269.8895764024439,
      "capital": 322.5701415806479,
      "insurance": 12.5
  },
  {
      "year": 4,
      "interests": 258.41673935506986,
      "capital": 334.0429786280219,
      "insurance": 12.5
  },
  {
      "year": 5,
      "interests": 246.53584845227522,
      "capital": 345.92386953081655,
      "insurance": 12.5
  },
  {
      "year": 6,
      "interests": 234.2323904617846,
      "capital": 358.2273275213072,
      "insurance": 12.5
  },
  {
      "year": 7,
      "interests": 221.49133595987348,
      "capital": 370.9683820232183,
      "insurance": 12.5
  },
  {
      "year": 8,
      "interests": 208.29712097201084,
      "capital": 384.16259701108095,
      "insurance": 12.5
  },
  {
      "year": 9,
      "interests": 194.6336279605159,
      "capital": 397.82609002257584,
      "insurance": 12.5
  },
  {
      "year": 10,
      "interests": 180.4841661360035,
      "capital": 411.97555184708824,
      "insurance": 12.5
  },
  {
      "year": 11,
      "interests": 165.83145106856787,
      "capital": 426.6282669145239,
      "insurance": 12.5
  },
  {
      "year": 12,
      "interests": 150.6575835737981,
      "capital": 441.80213440929367,
      "insurance": 12.5
  },
  {
      "year": 13,
      "interests": 134.9440278478334,
      "capital": 457.51569013525835,
      "insurance": 12.5
  },
  {
      "year": 14,
      "interests": 118.67158882474946,
      "capital": 473.7881291583423,
      "insurance": 12.5
  },
  {
      "year": 15,
      "interests": 101.82038872861536,
      "capital": 490.6393292544764,
      "insurance": 12.5
  },
  {
      "year": 16,
      "interests": 84.36984279157888,
      "capital": 508.0898751915129,
      "insurance": 12.5
  },
  {
      "year": 17,
      "interests": 66.29863410831832,
      "capital": 526.1610838747735,
      "insurance": 12.5
  },
  {
      "year": 18,
      "interests": 47.584687596143446,
      "capital": 544.8750303869483,
      "insurance": 12.5
  },
  {
      "year": 19,
      "interests": 28.20514302893662,
      "capital": 564.2545749541551,
      "insurance": 12.5
  },
  {
      "year": 20,
      "interests": 8.136327111993609,
      "capital": 584.3233908710981,
      "insurance": 12.5
  }
]

export type MonthlyPaymentGraphData = {
    insurance: number;
    interests: number;
    capital: number;
    year: number;
}

export type MonthlyPaymentsGraphProps = {
  graphData: MonthlyPaymentGraphData[];
  monthlyPayments: number;
  nbYears: number;
  displayPlaceholder?: boolean;
}



export const MonthlyPaymentsGraph = ({ graphData, monthlyPayments, nbYears, displayPlaceholder }: MonthlyPaymentsGraphProps) => {
  const { formatMessage } = useIntl();

  return (
    <div style={{
    }}>
      <GraphContainer height={264} width="100%">
        <BarChart
          data={displayPlaceholder ? PLACEHOLDER_DATA : graphData}
          margin={{
            right: 24,
          }}
          barCategoryGap={4}
          
        >
          <YAxis
            tickLine={false}
            tick={{ fontSize: 10 }}
            axisLine={false}
            domain={[0, 'dataMax']}
            tickFormatter={(props) => toCurrency(props, 'EUR', 0, { notation: 'compact' })}
          />
          {!displayPlaceholder ? (
            <Tooltip
              cursor={false}
              wrapperStyle={{ outline: 'none' }}
              content={({ payload }) => {
                if (!payload[0]) {
                  return null;
                }
                const data = payload[0].payload;

                return (
                  <MonthlyPaymentsGraphTooltip
                    data={data}
                    monthlyPayments={monthlyPayments}
                  />
                );
              }}
            />
          ) : null}
          <Bar dataKey="capital" stackId="a" fill={displayPlaceholder ? '#333333' : '#E54948'} />
          <Bar dataKey="interests" stackId="a" fill={displayPlaceholder ? '#333333' : palette.sky400} />
          <Bar dataKey="insurance" stackId="a" fill={displayPlaceholder ? '#333333' : palette.brand500} />
        </BarChart>
      </GraphContainer>
      <AxisValues>
        <Typography variant="bodyXS" color="webappTertiary">
          {formatMessage({ id: 'wealth_simulator.chart.axis_min' })}
        </Typography>
        <Typography variant="bodyXS" color="webappTertiary">{`${nbYears} ${formatMessage({
          id: 'wealth_simulator.chart.axis_max',
        })}`}</Typography>
      </AxisValues>
      <SimulatorLegend items={[
        {
            label: formatMessage({ id: 'monthly_payments_simulator.chart.legend.capital' }),
            color: '#E54948',
        },
        {
            label: formatMessage({ id: 'monthly_payments_simulator.chart.legend.interests' }),
            color: palette.sky400,
        },
        {
          label: formatMessage({ id: 'monthly_payments_simulator.chart.legend.insurance' }),
          color: palette.brand500,
      }
      ]} />
    </div>
  );
};

const GraphContainer = styled(ResponsiveContainer, {
  width: '100%',
});

const AxisValues = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '8px 30px 16px',
});